import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { Autocomplete, Alert, Box, Button, Center, Group, Loader, Stack, Text, type OptionsFilter } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { LLM_CAPABILITY_CATALOG, type ModelSettings } from '@bmai/shared';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { OperatorSettingsViewModel } from './OperatorSettingsViewModel';

/**
 * Fallback Bedrock model suggestions used when the live catalog (telemetry's
 * nightly `bedrock_model_metadata` refresh) is empty/unreachable. Free-text is
 * always allowed — the operator may paste any inference-profile / model id.
 */
const MODEL_SUGGESTIONS = [
    'us.amazon.nova-micro-v1:0',
    'us.amazon.nova-lite-v1:0',
    'us.amazon.nova-pro-v1:0',
    'us.anthropic.claude-haiku-4-5-20251001-v1:0',
];

/**
 * The service-side default model per capability — shown pre-filled when the
 * operator hasn't overridden it, so the field reflects the model actually in use.
 * Mirrors ClassifyLlmSettings (classification) and host-integrations LLM_DEFAULTS
 * (ai_assist / insights).
 */
const DEFAULT_MODELS: Record<string, string> = {
    classification: 'us.amazon.nova-lite-v1:0',
    ai_assist: 'us.anthropic.claude-haiku-4-5-20251001-v1:0',
    insights: 'us.anthropic.claude-haiku-4-5-20251001-v1:0',
};

/**
 * Show the whole catalog on focus. The field is pre-filled with a full model id,
 * so Autocomplete's default substring filter would collapse the dropdown to that
 * single value — this keeps every model selectable regardless of the current text.
 */
const showAllOptions: OptionsFilter = ({ options }) => options;

function Section({ title, description, children }: { title: string; description: string; children: ReactNode }): ReactElement {
    return (
        <Box>
            <Text size="sm" fw={600}>{title}</Text>
            <Text size="xs" c="dimmed" mb="sm">{description}</Text>
            {children}
        </Box>
    );
}

export default function OperatorModelSettingsPage(): ReactElement {
    const vm = useViewModel(OperatorSettingsViewModel);
    const [draft, setDraft] = useState<Record<string, string>>({});

    useEffect(() => { void vm.loadModels(); }, [vm]);
    useEffect(() => {
        if (!vm.models) return;
        const next: Record<string, string> = {};
        for (const c of LLM_CAPABILITY_CATALOG) next[c.key] = vm.models[c.key] ?? DEFAULT_MODELS[c.key] ?? '';
        setDraft(next);
    }, [vm.models]);

    const save = async (): Promise<void> => {
        const models = {} as ModelSettings;
        for (const c of LLM_CAPABILITY_CATALOG) {
            const v = draft[c.key]?.trim();
            models[c.key] = v ? v : null;
        }
        try {
            await vm.saveModels(models);
            notifications.show({ color: 'green', message: 'Model settings saved' });
        } catch (e) {
            notifications.show({ color: 'red', title: 'Save failed', message: e instanceof Error ? e.message : 'Unexpected error' });
        }
    };

    const catalogIds = vm.modelCatalog.map((m) => m.modelId);
    const options = catalogIds.length > 0 ? Array.from(new Set(catalogIds)) : MODEL_SUGGESTIONS;

    /**
     * The classification picker groups models by prompt-cache support (from the
     * nightly catalog) so an operator can pick a cacheable model — classification
     * re-sends a large taxonomy prompt per call, which caching makes far cheaper.
     * Falls back to the flat list when the live catalog is empty.
     */
    const classificationData = ((): (string | { group: string; items: string[] })[] => {
        if (catalogIds.length === 0) return options;
        const cacheable = Array.from(new Set(vm.modelCatalog.filter((m) => m.supportsPromptCache === true).map((m) => m.modelId)));
        const rest = Array.from(new Set(vm.modelCatalog.filter((m) => m.supportsPromptCache !== true).map((m) => m.modelId)));
        return [
            ...(cacheable.length > 0 ? [{ group: 'Supports prompt caching', items: cacheable }] : []),
            ...(rest.length > 0 ? [{ group: 'No / unknown prompt caching', items: rest }] : []),
        ];
    })();

    return (
        <Box>
            <PageHeaderComponent title="Models" subtitle="The Bedrock model each capability uses. Clear a field to fall back to the service default." />

            {vm.error && <Alert color="red" title="Failed to load" mb="md">{vm.error}</Alert>}

            {vm.loading && !vm.models ? (
                <Center py="xl"><Loader size="sm" /></Center>
            ) : (
                <Stack gap="xl">
                    {LLM_CAPABILITY_CATALOG.map((c) => (
                        <Section key={c.key} title={c.label} description={c.description}>
                            <Autocomplete
                                aria-label={`${c.label} model`}
                                placeholder="Service default"
                                data={c.key === 'classification' ? classificationData : options}
                                filter={showAllOptions}
                                value={draft[c.key] ?? ''}
                                onChange={(v) => setDraft((d) => ({ ...d, [c.key]: v }))}
                                maw={520}
                            />
                        </Section>
                    ))}

                    {catalogIds.length === 0 && (
                        <Text size="xs" c="dimmed">Live model catalog unavailable — showing common models. Any Bedrock model id can be entered.</Text>
                    )}

                    <Group>
                        <Button loading={vm.saving} onClick={() => void save()}>Save models</Button>
                    </Group>
                </Stack>
            )}
        </Box>
    );
}
